import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Grid2, InputAdornment, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getReferenceTableById } from "../../../api/reference";
import { getVariables } from "../../../api/variables";
import { useReferenceData } from "../../../hooks";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { getMonitoringRecordFormSchema, MonitoringRecordForm as MonitoringRecordFormType } from "../../../types/forms/capture";
import { Monitoring } from "../../../types/model/monitoring";
import { VariableResponse } from "../../../types/model/variable";
import { VARIABLE_CATEGORY_CODES } from "../../../types/model/variable/VariableCategories";
import FormAutocomplete from "../../General/FormAutocomplete";

interface MonitoringRecordFormProps {
  monitoringRecordData?: Monitoring;
  formId: string;
  isEditing: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSubmit: (values: MonitoringRecordFormType) => Promise<void>;
}

export default function MonitoringRecordForm({
  monitoringRecordData,
  formId,
  isEditing,
  isSaving,
  isDeleting,
  onSubmit,
}: Readonly<MonitoringRecordFormProps>) {
  const { t, i18n } = useTranslation();
  const handleError = useErrorHandler();
  const formStyles = useFormStyles(isEditing);

  const validationSchema = getMonitoringRecordFormSchema(t);
  const methods = useForm<MonitoringRecordFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      variableId: monitoringRecordData?.variableId ?? undefined,
      measuredAt: monitoringRecordData?.measuredAt ?? undefined,
      value: monitoringRecordData?.value ?? undefined,
      flag: monitoringRecordData?.flag ?? undefined,
      notes: monitoringRecordData?.notes ?? undefined
    },
  });

  const [variables, setVariables] = useState<VariableResponse[]>([]);

  useEffect(() => {
    const fetchVariables = async () => {
      try {
        const data = await getVariables(false, VARIABLE_CATEGORY_CODES.MONITORING);
        setVariables(data);
      } catch (error) {
        handleError(error);
      }
    };
    fetchVariables();
  }, []);
  
  const variableId = methods.watch("variableId");
  const selectedVariable = variables.find((v: any) => v.id === variableId);
  const [lookupTableInfo, setLookupTableInfo] = useState<{ schemaName: string, tableName: string } | null>(null);

  useEffect(() => {
    if (selectedVariable?.isLookup && selectedVariable.lookupTableId) {
      getReferenceTableById(selectedVariable.lookupTableId)
        .then((data) => setLookupTableInfo({schemaName: data.schemaName, tableName: data.tableName}) )
        .catch(error => {
          handleError(error);
          setLookupTableInfo(null);
        });
    } else {
      setLookupTableInfo(null);
    }
  }, [selectedVariable, methods]);
  
  const { data: lookupOptions = [] } = useReferenceData(
    lookupTableInfo?.schemaName as string,
    lookupTableInfo?.tableName as string
  );

  const handleFormSubmit = async (values: MonitoringRecordFormType) => {
    await onSubmit(values);
  };

  return (
    <FormProvider {...methods}>
      <Box component="form" id={formId} onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={4}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Controller
                name="measuredAt"
                control={methods.control}
                render={({ field }) => {
                  const value = field.value
                    ? dayjs(field.value, "YYYY-MM-DD HH:mm:ss")
                    : null;
                  
                  const isEn = i18n.language === "en";

                  return (
                    <DateTimePicker
                      label={t("flow-tests.flow-test-measurement.measured-at")}
                      value={value}
                      format={isEn ? "DD/MM/YYYY, hh:mm A" : "DD/MM/YYYY, HH:mm"}
                      ampm={isEn}
                      onChange={(date) => {
                        field.onChange(
                          date ? date.format("YYYY-MM-DD HH:mm:ss") : null
                        );
                      }}
                      timeSteps={{ seconds: 1 }}
                      disabled={!isEditing || isSaving || isDeleting}
                      sx={{ ...formStyles }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: isEditing ? "outlined" : "standard",
                          error: !!methods.formState.errors.measuredAt,
                          helperText: methods.formState.errors.measuredAt?.message,
                        }
                      }}
                    />
                  );
                }}
              />
            </LocalizationProvider>
          </Grid2>

          <Grid2 size={4}>
            <FormAutocomplete
              name="variableId" 
              label={`${t("captures.capture.monitoring.variable")} *`} 
              options={variables}
              isEditing={isEditing}
              isSaving={isSaving}
              disabled={false}
              formStyles={formStyles}
            />
          </Grid2>

          {isEditing ? (
            <>
              <Grid2 size={1.3}>
                <TextField
                  fullWidth
                  label={t("captures.capture.monitoring.flag")}
                  variant={isEditing ? "outlined" : "standard"}
                  disabled={!isEditing || isSaving || isDeleting}
                  sx={{ ...formStyles }}
                  {...methods.register("flag")}
                  error={!!methods.formState.errors.flag}
                  helperText={methods.formState.errors.flag?.message}
                />
              </Grid2>

              <Grid2 size={2.7}>
                {selectedVariable?.isLookup ? (
                  <FormAutocomplete
                    name="value"
                    label={t("captures.capture.monitoring.value")}
                    options={lookupOptions}
                    isEditing={isEditing}
                    isSaving={isSaving}
                    disabled={!isEditing || isSaving || isDeleting || !variableId}
                    formStyles={formStyles}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label={`${t("captures.capture.monitoring.value")} *`}                       
                    variant={isEditing ? "outlined" : "standard"}
                    disabled={!isEditing || isSaving || isDeleting || !variableId}
                    sx={{ ...formStyles }}
                    {...methods.register(
                      "value",
                      { valueAsNumber: true }
                    )}
                    error={!!methods.formState.errors.value}
                    helperText={methods.formState.errors.value?.message}
                  />
                )}
              </Grid2>
            </>
          ) : (
            <Grid2 size={4}>
              <TextField
                label={`${t("captures.capture.monitoring.value")} *`}                       
                variant="standard"
                disabled 
                sx={{ 
                  ...formStyles, 
                  width: `${Math.max(40, (String(methods.getValues("value") ?? "").length + (selectedVariable?.unit?.length ?? 0) + (methods.getValues("flag")?.length ?? 0) + 1) * 10)}px`
                }}
                {...methods.register("value", { valueAsNumber: true })}
                slotProps={{
                  input: {
                    startAdornment: <InputAdornment position="start">{methods.getValues("flag")}</InputAdornment>,
                    endAdornment: <InputAdornment position="end">{selectedVariable?.unit}</InputAdornment>
                  },
                  inputLabel: { shrink: true }
                }}
              />
            </Grid2>
          )}  

          <Grid2 size={12}>
            <TextField
              fullWidth
              label={t("captures.capture.monitoring.notes")}
              placeholder={t("captures.capture.monitoring.notes-placeholder")}
              multiline
              minRows={6}
              variant={isEditing ? "outlined" : "standard"}
              disabled={!isEditing || isSaving || isDeleting}
              sx={{ ...formStyles }}
              {...methods.register("notes")}
              error={!!methods.formState.errors.notes}
              helperText={methods.formState.errors.notes?.message}
            />
          </Grid2>
        </Grid2>
      </Box>
    </FormProvider>
  );
}